import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Filter, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Loader2,
  QrCode,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TransactionHistory = () => {
  const { staff } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const canUpdateStatus = staff?.role === 'owner' || staff?.role === 'manager';

  const showPaymentQR = async (tx) => {
    setSelectedTx(tx);
    setQrModalOpen(true);
    
    // Fetch full transaction details if qr_code not present
    if (!tx.qr_code) {
      setQrLoading(true);
      try {
        const response = await axios.get(`${API}/transactions/${tx.transaction_id}`, { withCredentials: true });
        setSelectedTx(response.data);
      } catch (error) {
        console.error('Error fetching QR code:', error);
      } finally {
        setQrLoading(false);
      }
    }
  };

  const openConfirmModal = (tx) => {
    setSelectedTx(tx);
    setConfirmModalOpen(true);
  };

  const handleMarkAsConfirmed = async () => {
    if (!selectedTx) return;
    
    setConfirmLoading(true);
    try {
      await axios.patch(`${API}/transactions/${selectedTx.transaction_id}`, 
        { status: 'confirmed' },
        { withCredentials: true }
      );
      toast.success('Transaction marked as confirmed');
      setConfirmModalOpen(false);
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm transaction');
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [statusFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/transactions?${params}`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tx.transaction_id.toLowerCase().includes(query) ||
      tx.description.toLowerCase().includes(query) ||
      tx.reference.toLowerCase().includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-emerald-600 bg-emerald-50';
      case 'pending': return 'text-amber-600 bg-amber-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'manual_review': return 'text-blue-600 bg-blue-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle2 className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'manual_review': return <AlertTriangle className="w-4 h-4" />;
      default: return null;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="transaction-history-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -4px rgba(0,0,0,0.1); }
        .hover-scale { transition: transform 0.2s ease; }
        .hover-scale:hover { transform: scale(1.05); }
        .table-row-hover { transition: all 0.2s ease; }
        .table-row-hover:hover { background: linear-gradient(90deg, transparent 0%, rgba(20, 184, 166, 0.05) 50%, transparent 100%); }
      `}</style>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A1628]">Transaction History</h1>
          <p className="text-slate-500 mt-1">
            {filteredTransactions.length} transactions found
          </p>
        </div>
        <Link to="/app/transactions/new">
          <Button className="btn-accent transition-all duration-200 hover:scale-105 active:scale-95" data-testid="new-tx-btn">
            <Plus className="w-5 h-5 mr-2" />
            New Transaction
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 hover-lift">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 transition-colors group-focus-within:text-teal-500" />
            <Input
              type="text"
              placeholder="Search by ID, description, or reference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11 transition-all duration-200 hover:border-teal-500" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="manual_review">Manual Review</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover-lift">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0077B6]" />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-2">No transactions found</h3>
            <p className="text-slate-500">
              {searchQuery ? 'Try a different search term' : 'Create your first transaction to get started'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="transactions-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Transaction ID
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Description
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Network
                  </th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Amount
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Created
                  </th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransactions.map((tx, idx) => (
                  <tr 
                    key={tx.transaction_id} 
                    className="table-row-hover cursor-pointer"
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <td className="px-6 py-4">
                      <Link 
                        to={`/app/transactions/${tx.transaction_id}`}
                        className="text-[#0077B6] font-mono text-sm hover:underline transition-colors hover:text-teal-600"
                        data-testid={`tx-link-${tx.transaction_id}`}
                      >
                        {tx.transaction_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-700">{tx.description}</div>
                      <div className="text-xs text-slate-500">{tx.reference}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-transform hover:scale-105 ${
                        tx.network === 'MTN' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {tx.network}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-slate-700">
                        LRD {formatCurrency(tx.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(tx.status)}`}>
                        {getStatusIcon(tx.status)}
                        {tx.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {formatDate(tx.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canUpdateStatus && tx.status === 'pending' && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => openConfirmModal(tx)}
                            data-testid={`confirm-${tx.transaction_id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Confirm
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[#0077B6] border-[#0077B6]/30 hover:bg-[#0077B6]/10"
                          onClick={() => showPaymentQR(tx)}
                          data-testid={`show-qr-${tx.transaction_id}`}
                        >
                          <QrCode className="w-4 h-4 mr-1" />
                          QR
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment QR Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Payment QR Code</DialogTitle>
          </DialogHeader>
          
          {qrLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#0077B6]" />
            </div>
          ) : selectedTx && (
            <div className="text-center" data-testid="payment-qr-modal">
              {/* QR Code */}
              {selectedTx.qr_code ? (
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm">
                    <img 
                      src={`data:image/png;base64,${selectedTx.qr_code}`}
                      alt="Payment QR Code"
                      className="w-48 h-48"
                      data-testid="payment-qr-image"
                    />
                  </div>
                </div>
              ) : (
                <div className="py-8 text-slate-500">QR code not available</div>
              )}
              
              {/* Payment Details */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <div className="font-mono text-lg text-[#0077B6] font-bold mb-3">
                  {selectedTx.transaction_id}
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-left">
                    <span className="text-slate-500 block">Amount</span>
                    <span className="font-semibold text-slate-800 text-lg">
                      LRD {formatCurrency(selectedTx.amount)}
                    </span>
                  </div>
                  <div className="text-left">
                    <span className="text-slate-500 block">Network</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedTx.network === 'MTN' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {selectedTx.network}
                    </span>
                  </div>
                  <div className="text-left">
                    <span className="text-slate-500 block">Reference</span>
                    <span className="font-medium text-slate-700">{selectedTx.reference}</span>
                  </div>
                  <div className="text-left">
                    <span className="text-slate-500 block">Status</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(selectedTx.status)}`}>
                      {getStatusIcon(selectedTx.status)}
                      {selectedTx.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Instructions */}
              <div className="bg-blue-50 rounded-lg p-3 text-left text-sm">
                <p className="font-medium text-blue-800 mb-1">Payment Instructions:</p>
                <ol className="text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Open your {selectedTx.network} mobile money app</li>
                  <li>Scan the QR code above</li>
                  <li>Confirm the payment of LRD {formatCurrency(selectedTx.amount)}</li>
                  <li>Use reference: {selectedTx.reference}</li>
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Transaction Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Transaction</DialogTitle>
            <DialogDescription className="text-slate-500">
              Mark this transaction as confirmed after verifying the mobile money payment.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTx && (
            <div className="py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Transaction ID</span>
                  <span className="font-mono text-slate-800">{selectedTx.transaction_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-semibold text-slate-800">LRD {formatCurrency(selectedTx.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Reference</span>
                  <span className="text-slate-800">{selectedTx.reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Network</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    selectedTx.network === 'MTN' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {selectedTx.network}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800 text-sm">
                  <strong>Important:</strong> Only confirm this transaction after verifying that the mobile money payment has been received successfully.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmModalOpen(false)} className="text-slate-500">
              Cancel
            </Button>
            <Button
              onClick={handleMarkAsConfirmed}
              disabled={confirmLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-transaction-btn"
            >
              {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Mark as Confirmed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionHistory;
